import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useToast from '#/common/hooks/useToast';
import type { Alert, AlertState } from '#/graphql/types';
import { get } from 'lodash';
import { fields } from './fields';
import { states } from '../../utils/constants';
import useUpdateAlertMutation from '../../hooks/useUpdateAlertMutation';
import RecordPreview, { RecordAction } from '#/common/components/RecordPreview';
import Styles from './Styles';
import { Throbber } from '#/common/components/Form/Form';
import { PageAction, PageActions } from '#/common/components/SubPage';

export interface AlertRecordPreviewProps {
  alert: Alert;
}

function separateIgnoreFromActions(actions: RecordAction[]): {
  nonIgnoreActions: RecordAction[];
  ignoreAction?: RecordAction;
} {
  return {
    nonIgnoreActions: actions.filter((a) => a.type !== 'danger'),
    ignoreAction: actions.find((a) => a.type === 'danger'),
  };
}

const AlertRecordPreview: React.FC<AlertRecordPreviewProps> = ({ alert }) => {
  const { alert: showAlert, confirm } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { updateAlert } = useUpdateAlertMutation();

  const id = get(alert, 'id', undefined);
  const state = get(alert, 'state', undefined);
  const tenantId = get(alert, 'tenantId', undefined);
  const assetId = get(alert, 'assetId', undefined);
  const findingId = get(alert, 'findingId', undefined);
  const workOrderId = get(alert, 'workOrderId', undefined);
  const displayNumber = get(alert, 'displayNumber', undefined);

  const isNew = state === states.new.value;
  const noWoFinding = !workOrderId && !findingId;

  const title = 'Alert Details';

  const ignore = useCallback(
    (data: Alert) => {
      if (!id) return;

      return updateAlert(id, data);
    },
    [updateAlert],
  );

  const actions = useMemo<PageAction[]>(() => {
    if (!isNew) {
      return [];
    }
    const result: PageAction[] = [];

    if (noWoFinding) {
      result.push({
        id: 'create-wo',
        type: 'button',
        props: {
          label: 'Create Work Order',
          type: 'normal',
          onClick: async () => {
            navigate(`/work-orders/new?&assetId=${assetId}&tenantId=${tenantId}&alertId=${id}`);
          },
        },
      });

      result.push({
        id: 'create-finding',
        type: 'button',
        props: {
          label: 'Create Finding',
          type: 'normal',
          onClick: async () => {
            navigate(`/findings/new?&assetId=${assetId}&tenantId=${tenantId}&alertId=${id}`);
          },
        },
      });
    }

    result.push({
      id: 'ignore',
      alwaysShow: true,
      type: 'button',
      props: {
        type: 'danger',
        label: 'Ignore',
        onClick: async () => {
          const sure = await confirm(
            'error',
            `Are you sure you want to ignore alert ${displayNumber}?`,
          );
          if (!sure) return;
          setIsLoading(true);
          const result = await ignore({ ...alert, state: states.ignored.value as AlertState });
          setIsLoading(false);
          if (result?.errors) {
            void showAlert('error', result.errors.join(`;\n`));
            return;
          }
          void showAlert('success', `Alert ${displayNumber} ignored successfully!`);
          navigate('/alerts?filters=["state","anyof",["new","processed","resolved"]]');
        },
      },
    });

    return result;
  }, [showAlert, confirm, navigate, alert, title]);

  return (
    <Styles>
      {isLoading && <Throbber />}
      <PageActions actions={actions} />
      <RecordPreview record={alert} fields={fields} title={title} />
    </Styles>
  );
};

export default React.memo(AlertRecordPreview);
